import type {
  RevenueMetrics,
  RevenueTimeseriesPoint,
} from '@logspot-platform/platform-types';
import type { WidgetsHttpClient } from '../widgets-context';
import type {
  AggregationData,
  DauData,
  DayMetrics,
  EventDataMap,
  FilterGroup,
  RetentionData,
} from '../types';

// These mirror the admin app's services for the endpoints the shared widgets
// use (paths, payload shapes and query keys must stay in sync with
// apps/admin/src/api/* so both sides share the query cache when the admin
// prefetches in route loaders).

type GetDauParams = {
  projectId: string;
  interval: string;
  startDate: Date;
  endDate: Date;
  filterGroups?: FilterGroup[];
};

export const getDau = async (
  api: WidgetsHttpClient,
  { projectId, interval, startDate, endDate, filterGroups }: GetDauParams,
) => {
  const { data } = await api.post<{ data: DauData }>(`/analytics/dau`, {
    projectId,
    interval,
    startDate,
    endDate,
    filterGroups,
  });
  return data;
};

type GetAggregationByPropertyParams = {
  projectId: string;
  eventName?: string;
  metadataField?: string;
  field?: string;
  startDate?: Date;
  endDate?: Date;
  filterGroups?: FilterGroup[];
};

export const getAggregationByProperty = async (
  api: WidgetsHttpClient,
  {
    projectId,
    eventName,
    metadataField,
    field,
    startDate,
    endDate,
    filterGroups,
  }: GetAggregationByPropertyParams,
) => {
  const { data } = await api.post<{ data: AggregationData }>(
    `/analytics/aggregate-by-property`,
    {
      projectId,
      eventName,
      metadataField,
      field,
      startDate,
      endDate,
      filterGroups,
    },
  );
  return data;
};

type GetRevenueParams = {
  projectId: string;
  startDate?: Date;
  endDate?: Date;
};

const revenueQuery = ({ projectId, startDate, endDate }: GetRevenueParams) => {
  const search = new URLSearchParams({ projectId });
  if (startDate) search.set('startDate', startDate.toISOString());
  if (endDate) search.set('endDate', endDate.toISOString());
  return search.toString();
};

// Revenue reads are GET + query string, unlike the POST analytics endpoints,
// and are not filterable by filterGroups.
export const getRevenueMetrics = async (
  api: WidgetsHttpClient,
  params: GetRevenueParams,
) => {
  const { data } = await api.get<{ data: RevenueMetrics }>(
    `/revenue/metrics?${revenueQuery(params)}`,
  );
  return data;
};

export const getRevenueTimeseries = async (
  api: WidgetsHttpClient,
  params: GetRevenueParams,
) => {
  const { data } = await api.get<{
    data: { series: RevenueTimeseriesPoint[]; currency: string };
  }>(`/revenue/timeseries?${revenueQuery(params)}`);
  return data;
};

type GetRetentionParams = {
  projectId: string;
  startDate?: Date;
  endDate?: Date;
  filterGroups?: FilterGroup[];
};

export const getRetention = async (
  api: WidgetsHttpClient,
  { projectId, startDate, endDate, filterGroups }: GetRetentionParams,
) => {
  const { data } = await api.post<{ data: RetentionData }>(
    `/analytics/retention`,
    {
      projectId,
      startDate,
      endDate,
      filterGroups,
    },
  );
  return data;
};

function optionalQuery(name: string, value?: string | number | null): string {
  return value != null ? `${name}=${value}` : '';
}

type GetAllEventCountsParams = {
  projectId: string;
  userId?: string | null;
  interval: string;
  startDate?: number;
  endDate?: number;
  filterGroups?: FilterGroup[];
};

export const getAllEventCounts = async (
  api: WidgetsHttpClient,
  {
    projectId,
    userId,
    interval,
    startDate,
    filterGroups,
    endDate,
  }: GetAllEventCountsParams,
): Promise<EventDataMap> => {
  const query = [
    optionalQuery('project', projectId),
    optionalQuery('userId', userId),
    optionalQuery('startDate', startDate),
    optionalQuery('endDate', endDate),
    optionalQuery('interval', encodeURI(interval)),
  ]
    .filter(Boolean)
    .join('&');

  const { data } = await api.post<{ data: DayMetrics[] }>(
    `/events/counts?${query}`,
    { filter_groups: filterGroups },
  );

  return data.reduce((acc: Record<string, DayMetrics[]>, entry: DayMetrics) => {
    const dayMetrics = {
      name: entry.name,
      date: entry.date,
      count: entry.count,
    };
    const existing = acc[entry.name];
    acc[entry.name] = existing ? [...existing, dayMetrics] : [dayMetrics];
    return acc;
  }, {});
};
